import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';
import {
    BarElement, CategoryScale, Chart as ChartJS, Legend, LinearScale, Tooltip
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { getCashFlow, getCashflowSummary } from '../../services/cashflow';
import { onQuerySuccess } from '../../utils/queryResponse';
import { rsdFormat } from '../../utils/helper';
import { SkeletonChart, SkeletonRows } from '../../components/skeleton';
import OverlayLoader from '../../components/overlayLoader';
import { subYears, subMonths } from 'date-fns';

ChartJS.register(CategoryScale, LinearScale, BarElement, Tooltip, Legend);

const pageStyles = `
  .sbr .cf-chart{position:relative;padding:6px 2px 0}
  .sbr .cf-cap{display:flex;gap:26px;flex-wrap:wrap;margin-top:16px;padding-top:15px;border-top:1px solid var(--line)}
  .sbr .cf-cap .cell .k{font-size:11px;font-weight:700;color:var(--muted);text-transform:uppercase;letter-spacing:.5px;display:flex;align-items:center;gap:7px}
  .sbr .cf-cap .cell .k i{width:10px;height:10px;border-radius:3px;display:inline-block}
  .sbr .cf-cap .cell .vv{font-size:18px;font-weight:800;color:var(--ink);letter-spacing:-.5px;margin-top:5px;font-variant-numeric:tabular-nums}
  .sbr .cf-cap .cell .vv.net{color:var(--good)}

  .sbr .legend-row{display:flex;gap:16px;font-size:12.5px;font-weight:600;color:var(--text)}
  .sbr .legend-row span{display:flex;align-items:center;gap:6px}
  .sbr .legend-row i{width:10px;height:10px;border-radius:3px;display:inline-block}

  .sbr .cf-tbl{width:100%;border-collapse:collapse;font-size:13.5px}
  .sbr .cf-tbl thead th{text-align:left;font-weight:700;color:var(--muted);font-size:11px;text-transform:uppercase;
    letter-spacing:.5px;padding:12px 18px;border-bottom:1px solid var(--line);background:var(--surface)}
  .sbr .cf-tbl thead th.r{text-align:right}
  .sbr .cf-tbl tbody td{padding:12px 18px;border-bottom:1px solid var(--line-soft);color:var(--text);vertical-align:middle}
  .sbr .cf-tbl tbody tr:hover{background:var(--brand-50)}
  .sbr .cf-tbl .r{text-align:right}
  .sbr .cf-tbl .money{font-variant-numeric:tabular-nums;font-weight:700;color:var(--ink)}
  .sbr .cf-tbl .mlab{font-weight:700;color:var(--ink);font-size:13.5px}
  .sbr .cf-tbl .mlab small{display:block;font-weight:600;color:var(--faint);font-size:11px;margin-top:2px}
  .sbr .cf-tbl td.net{font-variant-numeric:tabular-nums;font-weight:800;font-size:14px}
  .sbr .cf-tbl td.net.pos{color:var(--good)}
  .sbr .cf-tbl td.net.neg{color:var(--bad)}
  .sbr .cf-tbl td.net .arrow{display:inline-flex;width:18px;height:18px;border-radius:6px;align-items:center;
    justify-content:center;margin-right:8px;vertical-align:-4px}
  .sbr .cf-tbl td.net.pos .arrow{background:var(--good-bg);color:var(--good)}
  .sbr .cf-tbl td.net.neg .arrow{background:var(--bad-bg);color:var(--bad)}
  .sbr .cf-tbl td.net .arrow svg{width:11px;height:11px}
  .sbr .cf-tbl tfoot td{padding:14px 18px;border-top:2px solid var(--line);font-weight:800;color:var(--ink);
    font-variant-numeric:tabular-nums;background:var(--surface)}
  .sbr .cf-tbl tfoot td.r{text-align:right}
  .sbr .cf-tbl tfoot .lbl{font-size:11px;text-transform:uppercase;letter-spacing:.5px;color:var(--muted)}
  .sbr .cf-tbl tfoot td.net{color:var(--good);font-size:15px}
  .sbr .cf-tbl tfoot td.net.neg{color:var(--bad)}

  .sbr .per-seg{display:flex;background:var(--surface);border-radius:10px;padding:3px;border:1px solid var(--line);gap:2px}
  .sbr .per-seg button{font:inherit;font-size:12.5px;font-weight:600;border:none;border-radius:8px;padding:5px 13px;cursor:pointer;transition:all .15s}

  /* horizontal scroll wrapper so the wide table can be swiped on mobile instead of being clipped */
  .sbr .cf-scroll{overflow-x:auto;-webkit-overflow-scrolling:touch}
  .sbr .cf-scroll::-webkit-scrollbar{height:6px}
  .sbr .cf-scroll::-webkit-scrollbar-thumb{background:var(--line);border-radius:999px}
  .sbr .cf-scroll::-webkit-scrollbar-track{background:transparent}
  @media(max-width:899px){.sbr .cf-tbl{min-width:640px}}
`;

const PERIOD_KEYS = ['Weekly', 'Monthly', 'Yearly'];

const BAR_OPTIONS = {
    maintainAspectRatio: false,
    responsive: true,
    interaction: { mode: 'index', intersect: false },
    plugins: {
        legend: { display: false },
        tooltip: {
            backgroundColor: '#fff',
            borderColor: '#E7E9EE',
            borderWidth: 1,
            titleColor: '#0F172A',
            bodyColor: '#334155',
            titleFont: { size: 12, weight: '700' },
            bodyFont: { size: 12 },
            padding: 12,
            boxPadding: 5,
            cornerRadius: 10,
            callbacks: {
                label: (ctx) => ` ${ctx.dataset.label}: ${ctx.parsed.y.toLocaleString('sr-RS', { minimumFractionDigits: 2 })} RSD`,
            },
        },
    },
    scales: {
        x: {
            grid: { display: false },
            ticks: { font: { size: 11 }, color: '#94A3B8', maxRotation: 0, padding: 6 },
            border: { display: false },
        },
        y: {
            grid: { color: '#F1F5F9', drawBorder: false },
            ticks: {
                font: { size: 11 }, color: '#94A3B8', padding: 10,
                callback: (v) => v >= 1000000 ? (v/1000000).toFixed(1)+'M' : v >= 1000 ? (v/1000).toFixed(0)+'k' : v,
            },
            border: { display: false },
        },
    },
    borderRadius: 5,
    barPercentage: 0.7,
    categoryPercentage: 0.65,
};

const BarChart = ({ chartData, isLoading }) => {
    const { t } = useTranslation();
    if (isLoading && !chartData) return <SkeletonChart />;
    if (!chartData) return <div style={{ height: '280px' }} />;

    const labels  = chartData.income?.labels || chartData.expenses?.labels || [];
    const incData = chartData.income?.data   || [];
    const expData = chartData.expenses?.data || [];

    const data = {
        labels,
        datasets: [
            { label: t('cashflow.income'),   data: incData, backgroundColor: '#3B74F2', hoverBackgroundColor: '#1D4FD8' },
            { label: t('cashflow.expenses'), data: expData, backgroundColor: '#FBBF24', hoverBackgroundColor: '#F59E0B' },
        ],
    };

    return (
        <div style={{ height: '280px', position: 'relative' }}>
            <Bar options={BAR_OPTIONS} data={data} />
        </div>
    );
};

const CashFlowPage = () => {
    const { t } = useTranslation();

    const [chartData, setChartData]   = useState(null)
    const [tableData, setTableData]   = useState([])
    const [period, setPeriod]         = useState('Monthly')

    const getDateRange = (p) => ({
        start: p === 'Yearly'  ? subYears(new Date(), 6)
             : p === 'Weekly'  ? subMonths(new Date(), 4)
             : subYears(new Date(), 1),
        end: new Date()
    })
    const [dateRange, setDateRange] = useState(() => getDateRange('Monthly'))

    const { data: periodData, isFetching: periodLoading } = useQuery(
        ['cash-flow', period, dateRange.start, dateRange.end],
        () => getCashFlow(period, dateRange.start, dateRange.end),
        { staleTime: 0 }
    )

    const { data: summaryData } = useQuery(
        ['cash-flow-summary'],
        () => getCashflowSummary(),
        { staleTime: 60000 }
    )

    const handlePeriod = (p) => {
        setPeriod(p)
        setDateRange(getDateRange(p))
        setChartData(null)
        setTableData([])
    }

    useEffect(() => {
        onQuerySuccess(periodData, (result) => {
            setChartData(result.chart)
            setTableData(result.tabular)
        })
    }, [periodData])

    const totalIncome   = tableData.reduce((s, d) => s + (d.totalIncome   || 0), 0)
    const totalExpenses = tableData.reduce((s, d) => s + (d.totalExpenses || 0), 0)
    const netTotal      = totalIncome - totalExpenses
    const outstanding   = summaryData?.outstanding ?? null
    const unpaidCount   = summaryData?.unpaidCount ?? null
    const netMargin     = totalIncome > 0 ? ((netTotal / totalIncome) * 100).toFixed(1) : null
    const expRatio      = totalIncome > 0 ? ((totalExpenses / totalIncome) * 100).toFixed(1) : null

    const bestMonth = tableData.reduce((best, d) => {
        const net = d.net || (d.totalIncome - d.totalExpenses)
        return (!best || net > best.net) ? { label: d.label, net } : best
    }, null)

    const PERIOD_LABEL_MAP = { Weekly: 'cashflow.weekly', Monthly: 'cashflow.monthlyPeriod', Yearly: 'cashflow.yearly' }
    const periodLabel = period === 'Weekly'  ? t('cashflow.lastNWeeks', { count: 16 })
                      : period === 'Yearly'  ? t('cashflow.lastNYears', { count: 6 })
                      : t('cashflow.lastNMonths', { count: 12 })

    return (
        <div className="sbr">
            <style>{pageStyles}</style>

            {/* ── Desktop header ── */}
            <div className="dhead sbr-d">
                <div>
                    <div className="ttl">{t('cashflow.title')}</div>
                    <div className="sub">{periodLabel} · {t('cashflow.allFiguresInCurrency')}</div>
                </div>
                <div className="act">
                    <div className="per-seg">
                        {PERIOD_KEYS.map(p => (
                            <button key={p} onClick={() => handlePeriod(p)}
                                style={{
                                    color:      period === p ? '#fff'       : 'var(--muted)',
                                    background: period === p ? 'var(--ink)' : 'transparent',
                                    boxShadow:  period === p ? 'var(--sh-1)' : 'none',
                                    fontWeight: period === p ? 700 : 500,
                                }}>
                                {t(PERIOD_LABEL_MAP[p])}
                            </button>
                        ))}
                    </div>
                    <button className="btn-sbr btn-ghost-sbr btn-sm-sbr">
                        <svg className="ic" style={{ width:15, height:15 }} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                            <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                        </svg>
                        {t('common.export')}
                    </button>
                </div>
            </div>

            {/* ── Mobile appbar ── */}
            <div className="sbr-m" style={{ marginBottom: '16px' }}>
                <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
                    <div style={{ flex:1, fontSize:'18px', fontWeight:800, color:'var(--ink)', letterSpacing:'-.4px' }}>{t('cashflow.title')}</div>
                    <div className="per-seg">
                        {PERIOD_KEYS.map(p => (
                            <button key={p} onClick={() => handlePeriod(p)} style={{ fontSize:'11px', padding:'4px 9px',
                                color:      period === p ? '#fff'       : 'var(--muted)',
                                background: period === p ? 'var(--ink)' : 'transparent',
                                fontWeight: period === p ? 700 : 500 }}>
                                {t(PERIOD_LABEL_MAP[p])[0]}
                            </button>
                        ))}
                    </div>
                </div>
            </div>

            {/* ── KPI tiles ── */}
            <div className="kpis4" style={{ position: 'relative' }}>
                {/* Income */}
                <div className="kpi">
                    <div className="l">
                        <svg className="ic" style={{ color: 'var(--good)' }} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <line x1="7" y1="17" x2="17" y2="7"/><polyline points="7 7 17 7 17 17"/>
                        </svg>
                        {t('cashflow.incomeKpi')}
                    </div>
                    <div className="n">{rsdFormat(totalIncome, false)} <small style={{ fontSize:'13px', color:'var(--muted)', fontWeight:700 }}>RSD</small></div>
                    <div className="d up">{expRatio ? t('cashflow.nPeriods', { count: tableData.length, period: t(PERIOD_LABEL_MAP[period]).toLowerCase() }) : t('common.loading')}</div>
                </div>

                {/* Expenses */}
                <div className="kpi">
                    <div className="l">
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/>
                        </svg>
                        {t('cashflow.expensesKpi')}
                    </div>
                    <div className="n">{rsdFormat(totalExpenses, false)} <small style={{ fontSize:'13px', color:'var(--muted)', fontWeight:700 }}>RSD</small></div>
                    <div className="d flat">{expRatio ? t('cashflow.ofIncome', { ratio: expRatio }) : t('common.loading')}</div>
                </div>

                {/* Net balance — lift */}
                <div className="kpi lift">
                    <div className="l">
                        <svg className="ic" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <line x1="12" y1="2" x2="12" y2="22"/><path d="M5 7l7-4 7 4M5 7v10l7 4 7-4V7"/>
                        </svg>
                        {t('cashflow.netBalance')}
                    </div>
                    <div className="n" style={{ color: netTotal >= 0 ? '#6EE7B7' : 'var(--bad)' }}>
                        {netTotal >= 0 ? '+' : ''}{rsdFormat(netTotal, false)} <small style={{ fontSize:'13px', fontWeight:700, opacity:.7 }}>RSD</small>
                    </div>
                    <div className="d" style={{ color: netTotal >= 0 ? '#6EE7B7' : 'var(--bad)' }}>
                        {netTotal >= 0 ? `▲ ${t('cashflow.positive')}` : `▼ ${t('cashflow.negative')}`}{netMargin ? ` · ${t('cashflow.margin', { value: netMargin })}` : ''}
                    </div>
                </div>

                {/* Outstanding */}
                <div className="kpi" style={{ borderColor: outstanding != null ? 'var(--warn-bd)' : undefined }}>
                    <div className="l">
                        <svg className="ic" style={{ color: outstanding != null ? 'var(--warn)' : undefined }} viewBox="0 0 24 24" fill="none" stroke="currentColor">
                            <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
                        </svg>
                        {t('cashflow.outstanding')}
                    </div>
                    <div className="n" style={{ color: outstanding != null ? 'var(--warn)' : 'var(--faint)' }}>
                        {outstanding != null ? rsdFormat(outstanding, false) : '—'}
                        {outstanding != null && <> <small style={{ fontSize:'13px', fontWeight:700, color:'var(--warn)' }}>RSD</small></>}
                    </div>
                    <div className="d" style={{ color: outstanding != null ? 'var(--warn)' : 'var(--muted)' }}>
                        {outstanding != null
                            ? `${unpaidCount != null ? t('cashflow.invoicesUnpaid', { count: unpaidCount }) : t('cashflow.unpaidInvoices')}`
                            : t('cashflow.noOutstandingData')}
                    </div>
                </div>

                {periodLoading && <OverlayLoader />}
            </div>

            {/* ── Chart card ── */}
            <div className="card-sbr" style={{ marginBottom: '18px' }}>
                <div className="ch">
                    <svg className="ci" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <path d="M18 20V10M12 20V4M6 20v-6"/>
                    </svg>
                    <span className="t">{t('cashflow.incomeVsExpenses')}</span>
                    <span className="more" style={{ marginLeft: 'auto' }}>{periodLabel}</span>
                </div>

                <div className="cf-chart">
                    <div className="legend-row" style={{ marginBottom: '12px' }}>
                        <span><i style={{ background: '#3B74F2' }}></i>{t('cashflow.incomeKpi')}</span>
                        <span><i style={{ background: '#FBBF24' }}></i>{t('cashflow.expensesKpi')}</span>
                    </div>
                    <BarChart chartData={chartData} isLoading={periodLoading} />
                    <div className="cf-cap">
                        <div className="cell">
                            <div className="k"><i style={{ background: '#1D4FD8' }}></i>{t('cashflow.totalIncome')}</div>
                            <div className="vv">{rsdFormat(totalIncome)} RSD</div>
                        </div>
                        <div className="cell">
                            <div className="k"><i style={{ background: '#F59E0B' }}></i>{t('cashflow.totalExpenses')}</div>
                            <div className="vv">{rsdFormat(totalExpenses)} RSD</div>
                        </div>
                        <div className="cell">
                            <div className="k"><i style={{ background: 'var(--good)' }}></i>{t('cashflow.netPeriod', { period: t(PERIOD_LABEL_MAP[period]).toLowerCase() })}</div>
                            <div className="vv net">{netTotal >= 0 ? '+' : ''}{rsdFormat(netTotal)} RSD</div>
                        </div>
                        {bestMonth && (
                            <div className="cell" style={{ marginLeft: 'auto' }}>
                                <div className="k">{t('cashflow.bestPeriod')}</div>
                                <div className="vv">{bestMonth.label} · {bestMonth.net >= 0 ? '+' : '−'}{rsdFormat(Math.abs(bestMonth.net))} RSD</div>
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Monthly cash flow table ── */}
            <div className="card-sbr" style={{ padding: 0, overflow: 'hidden' }}>
                <div className="ch" style={{ padding: '18px 18px 0' }}>
                    <svg className="ci" viewBox="0 0 24 24" fill="none" stroke="currentColor">
                        <line x1="12" y1="1" x2="12" y2="23"/>
                        <path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/>
                    </svg>
                    <span className="t">{t('cashflow.periodCashflow', { period: t(PERIOD_LABEL_MAP[period]) })}</span>
                    <span className="more" style={{ marginLeft: 'auto' }}>{t('cashflow.netEquation')}</span>
                </div>

                <div style={{ position: 'relative', minHeight: '80px' }}>
                    {periodLoading && tableData.length === 0 ? (
                        <div style={{ padding: '16px 18px' }}>
                            <SkeletonRows rows={6} widths={['28%', '22%', '22%', '18%']} />
                        </div>
                    ) : tableData.length === 0 ? (
                        <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--muted)', fontSize: '14px', fontStyle: 'italic' }}>
                            {t('cashflow.noDataForPeriod')}
                        </div>
                    ) : (
                        <div className="cf-scroll">
                        <table className="cf-tbl">
                            <thead>
                                <tr>
                                    <th>{t('cashflow.period')}</th>
                                    <th className="r">{t('cashflow.incomeRsd')}</th>
                                    <th className="r">{t('cashflow.expensesRsd')}</th>
                                    <th className="r">{t('cashflow.netRsd')}</th>
                                </tr>
                            </thead>
                            <tbody>
                                {tableData.filter(d => (d.totalIncome || 0) !== 0 || (d.totalExpenses || 0) !== 0).map((row, i) => {
                                    const net = row.net ?? (row.totalIncome - row.totalExpenses)
                                    const isPos = net >= 0
                                    return (
                                        <tr key={i}>
                                            <td><div className="mlab">{row.label}</div></td>
                                            <td className="r money">{rsdFormat(row.totalIncome)}</td>
                                            <td className="r money">{rsdFormat(row.totalExpenses)}</td>
                                            <td className={`net ${isPos ? 'pos' : 'neg'} r`}>
                                                <span className="arrow">
                                                    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                                                        {isPos
                                                            ? <><line x1="7" y1="17" x2="17" y2="7"/><polyline points="9 7 17 7 17 15"/></>
                                                            : <><line x1="7" y1="7" x2="17" y2="17"/><polyline points="9 17 17 17 17 9"/></>
                                                        }
                                                    </svg>
                                                </span>
                                                {isPos ? '+' : ''}{rsdFormat(Math.abs(net))}
                                            </td>
                                        </tr>
                                    )
                                })}
                            </tbody>
                            <tfoot>
                                <tr>
                                    <td><span className="lbl">{t('cashflow.totalPeriods', { count: tableData.filter(d => (d.totalIncome || 0) !== 0 || (d.totalExpenses || 0) !== 0).length, period: t(PERIOD_LABEL_MAP[period]).toLowerCase() })}</span></td>
                                    <td className="r">{rsdFormat(totalIncome)}</td>
                                    <td className="r">{rsdFormat(totalExpenses)}</td>
                                    <td className={`net r ${netTotal >= 0 ? 'pos' : 'neg'}`}>{netTotal >= 0 ? '+' : ''}{rsdFormat(Math.abs(netTotal))}</td>
                                </tr>
                            </tfoot>
                        </table>
                        </div>
                    )}
                    {periodLoading && tableData.length > 0 && <OverlayLoader />}
                </div>
            </div>

            <div className="spacer"></div>
        </div>
    );
};

export default CashFlowPage;
